import { Messages } from "@arcware-cloud/shared-pixelstreaming-websdk";
import {
  SettingFlag,
  SettingText,
  WebRtcPlayerController,
  Config,
  Flags,
} from "@epicgames-ps/lib-pixelstreamingfrontend-ue5.5";
import {
  Application,
  SettingUIFlag,
  SettingUIText,
  UIOptions,
  PixelStreamingApplicationStyle,
} from "@epicgames-ps/lib-pixelstreamingfrontend-ui-ue5.5";
import { ArcwarePixelStreaming } from "./ArcwarePixelStreaming";
import { ArcwareStyles } from "./styles/ArcwarePixelStreamingApplicationStyles";
import { AudioButton } from "./ui/AudioButton";
import { MicButton } from "./ui/MicButton";
import { StopButton } from "./ui/StopButton";

const DEBUG = false;

const TextKeyRegex = /^(\['([a-zA-Z 0-9-_]+)'\] )/;

function findElementByTextContent(root: HTMLElement | null, selector: string): HTMLElement | null {
  const match = TextKeyRegex.exec(selector);
  if (!match) return null;
  const actualSelector = selector.replace(match[1], "");
  const text = match[2].trim();

  const elements = (root || document).querySelectorAll<HTMLElement>(actualSelector);
  let result: HTMLElement | null = null;

  elements.forEach((element) => {
    const elementText: string = element?.innerHTML.trim();
    if (!result && element?.innerHTML?.includes(`>${text}<`)) {
      result = element;
    }
  });
  return result;
}

const removals: Record<string, boolean> = {
  // Is not used by Arcware system - may need additional checking.
  "#StreamerId": true,
  // Signalling-Server
  "#ss": true,
  // Is not working with our AFK Setup.
  "#TimeoutIfIdle": true,
  // OfferToReceive needs to be enabled in Arcware cloud environment.
  "#OfferToReceive": true,
  // SFU can't be handled in cloud environment.
  "#preferSFU": true,
  // STUN / TURN can't be handled in cloud environment.
  "#ForceTURN": true,
  // Arcware UI does not support Light or Dark mode.
  "#LightMode": true,
  // By default the UseMic setting should be disabled, since the won't work with 4.27 anyway.
  "#UseMic": true,
  // By default the MatchViewportRes setting should be disabled, since we have a custom method for that.
  "#MatchViewportRes": true,
  // By default the HoveringMouse setting should be disabled, since we have a custom method for that.
  "#HoveringMouse": true,
  // AFK Idle time.
  "['AFK timeout'] div.setting.form-group:not([id])": true,
  // By default the encoder settings should be disabled, since the won't work with 4.27 anyway.
  "['Encoder'] section.settingsContainer:not([id])": true,
  // By default the WebRTC settings should be disabled, since the won't work with 4.27 anyway.
  "['WebRTC'] section.settingsContainer:not([id])": true,
  // By default the UI settings should be disabled, since we have a custom method for them.
  "['UI'] section.settingsContainer:not([id])": true,
  // Hide option of start audio muted or not
  "#StartVideoMuted": true,
};

if (DEBUG)
  Object.keys(removals).forEach((key) => {
    removals[key] = false;
  });

function remove(key: string, element?: HTMLElement | null) {
  if (removals[key] === true) {
    if (element) {
      element.style.display = "none";
    }
  } else if (DEBUG) {
    if (element) {
      element.style.color = "red";
    }
  }
}

export class ArcwareApplication extends Application {
  static Flags = class {
    static noSession = "noSession" as const;
  };

  public override stream: ArcwarePixelStreaming;
  private videoElementParent: HTMLElement;
  private parentElement: HTMLElement | null;
  private responseCallback: ((response: string) => void) | null = null;
  private webRtcController: WebRtcPlayerController;

  override get rootElement(): HTMLElement {
    return super.rootElement;
  }

  private ArcwareSection: HTMLElement = this.configUI.buildSectionWithHeading(
    this.settingsPanel.settingsContentElement,
    "Arcware Cloud"
  );

  constructor(options: UIOptions & { stream: ArcwarePixelStreaming }) {
    super(options);
    this.stream = options?.stream;
    this.emitUIInteraction = this.emitUIInteraction.bind(this);

    this.adjustSettingsPanel();
    this.createAudioToggleButton();
    this.createMicToggleButton();
    this.createStopButton();
    this.applyArcwareStyles();

    this.videoElementParent = this?.stream?.videoElementParent;
    this.parentElement = this?.videoElementParent?.parentElement;
    this.webRtcController = this?.stream?.["_webRtcController"];
    
    this.addLoveLetterhandler()

    this?.stream?.addResponseEventListener("ue-response", (response) => this?.applicationResponse(response));

    this?.stream?.sessionIdHandler?.add((sessionId) => {
      this?.statsPanel?.addOrUpdateSessionStat("sessionId", "SessionId", sessionId);
    });

    this?.stream?.videoInitializedHandler?.add(() => {
      this?.currentOverlay?.hide();
      if (this?.stream?.streamInfo) this?.autoPlayHandler(this?.stream?.streamInfo);
    });

    const uiHandler = () => {
      // Remove fullscreen UI Default buttons from DOM
      this?.uiDefaultButtonVisibilty("fullscreenButton", "#fullscreen-btn");
      this?.uiDefaultButtonVisibilty("settingsButton", "#settingsBtn");
      this?.uiDefaultButtonVisibilty("infoButton", "#statsBtn");
      this?.uiDefaultButtonVisibilty("audioButton", "#audioBtn");
      this?.uiDefaultButtonVisibilty("stopButton", "#stopBtn");
      this?.uiDefaultButtonVisibilty("connectionStrengthIcon", "#connection");
      this?.uiDefaultButtonVisibilty("micButton", "#micBtn");

      this.uiElementsVisibility(true);
    };

    // Either on streamInfo
    // this?.stream.streamInfoHandler.add(uiHandler.bind(this));
    // Or on initialize.
    this?.stream?.videoInitializedHandler?.add(uiHandler.bind(this));

    this.uiElementsVisibility(false);
    this.addTextToConnectOverlay();
    this.preventDefaultKeyboardEvents();
  }

  public addLoveLetterhandler()
  {
    this?.stream?.loveLetterHandler?.add((loveLetter) => {
      if (this?.playOverlay && loveLetter && this?.parentElement) {
        const infoOverlay = this?.parentElement?.querySelector("#infoOverlay");
        if (infoOverlay) {
          infoOverlay?.classList?.remove("hiddenState");
          const message = infoOverlay?.querySelector("#messageOverlayInner");
          if (message) {
            message.innerHTML = loveLetter?.reason?.replace("LL: ", "");
          } 
        }
        // this.playOverlay.textElement.innerText = loveLetter.reason.replace("LL: ", "");
      }
    });
  }

  /** Set's and resets hidden state of "additional" UI Elements.
   * For example buttons or connection icon.
   * These will fade in through css animation upon the videoInitialized event.
   * At this point in time the streamInfo is already processed.
   */
  private uiElementsVisibility(state: boolean) {
    const controls = this?.parentElement?.querySelector<HTMLElement>("#controls");
    if (controls) {
      if (state) {
        controls?.classList?.remove("hidden");
        controls?.classList?.add("visible");
      } else {
        controls?.classList?.remove("visible");
        controls?.classList?.add("hidden");
      }
    }

    const connection = this?.parentElement?.querySelector<HTMLElement>("#connection");
    if (connection) {
      if (state) {
        connection?.classList?.remove("hidden");
        connection?.classList?.add("visible");
      } else {
        connection?.classList?.remove("visible");
        connection?.classList?.add("hidden");
      }
    }
  }

  private adjustSettingsPanel() {
    // Make Arcware Cloud settings the first element of the settings panel.
    const parent = this?.ArcwareSection?.parentElement;
    if (parent) {
      parent?.removeChild(this?.ArcwareSection);
      parent?.insertBefore(this?.ArcwareSection, parent?.firstChild);
    }

    // Remove all the settings specified by "removals" object.
    const SettingsContent = this?.settingsPanel?.settingsContentElement;
    if (SettingsContent) {
      SettingsContent.style.minWidth = "400px";

      for (const key in removals) {
        if (TextKeyRegex.test(key)) {
          const element = findElementByTextContent(SettingsContent, key);
          remove(key, element);
        } else {
          const element = SettingsContent.querySelector<HTMLElement>(key);
          remove(key, element);
        }
      }

      // Renaming the "Restart Stream" button to "Reconnect to Stream".
      const restartStreamButton = findElementByTextContent(SettingsContent, "['Restart Stream'] section > div.setting");
      if (restartStreamButton) {
        const label = restartStreamButton?.querySelector<HTMLElement>("div");
        if (label) label.textContent = "Reconnect to Stream";
        const button = restartStreamButton?.querySelector<HTMLInputElement>("label > input");
        if (button) button.value = "reconnect";
      }
    }

    // Append disclaimer
    {
      const disclaimer = document.createElement("div");
      disclaimer?.classList?.add("setting");
      disclaimer.textContent = "Keep in mind, these settings will be applied in your current session only.";
      disclaimer.style.paddingBottom = "15px";
      this?.ArcwareSection?.appendChild(disclaimer);
    }

    // Add disable SessionId button.
    this?.ArcwareSection?.appendChild(
      new SettingUIFlag(
        new SettingFlag(
          ArcwareApplication?.Flags?.noSession,
          "Disable SessionId",
          "Disable sessionId based reconnection.",
          this?.stream?.session?.noSession,
          true
        )
      ).rootElement
    );

    // Add rephrasing of signalling Url.
    this?.ArcwareSection?.appendChild(
      new SettingUIText(
        new SettingText(
          "signalling-url",
          "Signalling URL",
          "Signalling URL",
          `${this?.stream?.config?.getTextSettingValue("ss")}?${this?.stream?.config?.urlFlags?.slice(1)}`,
          false
        )
      ).rootElement
    );

    if (DEBUG)
      setTimeout(() => {
        document.querySelector<HTMLButtonElement>("#settingsBtn")?.click();
      }, 2000);
  }

  private createAudioToggleButton(): void {
    const audioButton = new AudioButton(this?.stream);
    const videoElementParent = this?.stream?.videoElementParent;
    const playerUI = videoElementParent!.parentElement;
    // Append the button to the videoElementParent
    if (playerUI) {
      const uiFeatures = playerUI?.querySelector("#uiFeatures");
      if (uiFeatures) {
        const uiControls = uiFeatures?.querySelector("#controls");
        if (uiControls) {
          uiControls?.appendChild(audioButton?.element);
        }
      }
    }
  }

  private createMicToggleButton(): void {
    const micButton = new MicButton(this?.stream);
    const videoElementParent = this?.stream?.videoElementParent;
    const playerUI = videoElementParent!.parentElement;
    // Append the button to the videoElementParent
    if (playerUI) {
      const uiFeatures = playerUI?.querySelector("#uiFeatures");
      if (uiFeatures) {
        const uiControls = uiFeatures?.querySelector("#controls");
        if (uiControls) {
          uiControls?.appendChild(micButton?.element);
        }
      }
    }
  }

  private createStopButton(): void {
    if (this.stream?.config?.settings?.stopButton === true) {
      const stopButton = new StopButton(this?.stream);
      const videoElementParent = this?.stream?.videoElementParent;
      const playerUI = videoElementParent!.parentElement;
      // Append the button to the videoElementParent
      if (playerUI) {
        const uiFeatures = playerUI?.querySelector("#uiFeatures");
        if (uiFeatures) {
          const uiControls = uiFeatures?.querySelector("#controls");
          if (uiControls) {
            uiControls?.appendChild(stopButton?.element);
          }
        }
      }
    }
  }

  private uiDefaultButtonVisibilty(buttonType: keyof typeof this.stream.config.settings, id: string) {
    if (this?.parentElement) {
      const button = this?.parentElement?.querySelector(id);
      if (this?.stream?.config?.settings[buttonType] === false) {
        if (button) {
          button?.remove();
          const uiFeaturesElement = this.uiFeaturesElement;
          const statsPanel = uiFeaturesElement.querySelector("#stats-panel");
          const settingsPanel = uiFeaturesElement.querySelector("#settings-panel");
          if (uiFeaturesElement) {
            if (buttonType === "infoButton") {
              statsPanel?.remove();
            }
            if (buttonType === "settingsButton") {
              settingsPanel?.remove();
            }
          }
        }
      }
      const isIOS = /iPad|iPhone|iPod/.test(navigator?.userAgent);
      if (buttonType === "fullscreenButton" && isIOS && button) {
        button?.remove();
      }
    }
  }

  private preventDefaultKeyboardEvents() {
    if (this.videoElementParent) {
      this.videoElementParent.addEventListener(
        "keydown",
        function (event) {
          if (event.key === "Tab" || event.key === " " || event.key === "Spacebar") {
            // Prevent the default action and stop propagation
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      ); // Use capturing instead of bubbling

      const buttons = this.uiFeaturesElement.querySelectorAll("button");
      buttons.forEach((button) => {
        button.addEventListener("keydown", function (event) {
          if (event.key === " " || event.key === "Spacebar") {
            event.preventDefault();
            button.blur();
          }
        });
      });
    }
  }

  private autoPlayHandler(streamInfo: Messages.StreamInfo) {
    const { videoPlayer } = this?.webRtcController;
    const autoPlay = streamInfo?.streamInfo?.autoPlay;
    const isPaused = videoPlayer?.isPaused();
    if (autoPlay && isPaused) {
      if (videoPlayer) {
        const videoElement = videoPlayer?.getVideoElement();
        if (videoElement) {
          const parentElement = videoElement?.parentElement;
          if (parentElement) {
            const playOverlay = parentElement?.querySelector("#playOverlay");
            if (playOverlay) playOverlay?.remove();
          }
          videoElement?.play();
        }
      }
    }
  }

  /** Request a response from you UE Application.
   * The returned response depends on the implementation of your UE Application.
   * It can be used to request information from the application, such as:
   * - a state to update your custom ui
   * - get the url to the pdf it uploaded to some remote service of yours
   * - the id of the save-game
   * .. anything else you could imagine.
   */
  public getApplicationResponse(callback: (response: string) => void): void {
    this?.stream?.addResponseEventListener("ue-response", (response) => {
      callback(response);
    });
  }

  public applicationResponse(response: string): void {
    if (this?.responseCallback) {
      this?.responseCallback(response);
    }
  }

  private applyArcwareStyles() {
    const PixelStreamingApplicationStyles = new PixelStreamingApplicationStyle(ArcwareStyles);
    PixelStreamingApplicationStyles?.applyStyleSheet();
  }

  /** Emit an event towards the UE Application.
   * Mainly used to bubble events like a button press or other command inputs towards the UE Application.
   */
  public emitUIInteraction(descriptor: object | string) {
    if (descriptor) {
      this?.webRtcController?.emitUIInteraction(descriptor);
    }
  }

  // Creates a helper text for the Connection Overlay
  private addTextToConnectOverlay() {
    if (!this?.stream?.config?.initialSettings?.AutoConnect) {
      if (this.videoElementParent) {
        const connectOverlay = this?.parentElement?.querySelector("#connectOverlay");
        if (connectOverlay) {
          const connectionText = document.createElement("div");
          connectionText.className = "connection-text";
          connectOverlay.appendChild(connectionText);

          const title = document.createElement("p");
          title.className = "title";
          title.textContent = "Click on the button below to start the connection to the streaming.";
          connectionText.appendChild(title);

          const subtitle = document.createElement("p");
          subtitle.className = "subtitle";
          subtitle.textContent =
            "After clicking the button, it may take a few seconds for the connection to start. Don't worry if you don't see an immediate reaction.";

          connectionText.appendChild(subtitle);
        }
      }
    }
  }
}
